/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { isNone } from '@ember/utils';
import { DragMode, VWBTab } from 'cio/utils/visual-workflow-builder/types';
import AuthService from 'cio/services/auth';
import VisualWorkflowBuilderService from 'cio/services/visual-workflow-builder';
import ActionModel from 'cio/models/action';
import { ParsedTreeBranchNode } from 'cio/models/campaign';
import AnalyticsService from 'cio/services/analytics';
import { ClonePluginOptions } from 'fly-drag-drop/plugins/draggable-clone';
import { AutoscrollPluginOptions } from 'fly-drag-drop/plugins/autoscroll';
import { tracked } from '@glimmer/tracking';
import { Dropdown as BasicDropdown } from 'ember-basic-dropdown/components/basic-dropdown';
import { ClickSource } from 'cio/utils/analytics/transforms/campaign';

interface VisualWorkflowBuilderNodeExitArgs {
  action: ActionModel;
  branch: ParsedTreeBranchNode;
  branchId: string;
  pathIndex: number;
  readOnly: boolean;
  selectedAction: ActionModel;
  tab: VWBTab;
  isEditing: boolean;
  onSelect: (action: ActionModel, event?: MouseEvent) => unknown;
  onActionClicked?: (event: MouseEvent, action: ActionModel) => unknown;
  onDragStart: (action: ActionModel) => unknown;
  onDragEnd: () => unknown;
  canDrag: boolean;
  isGrayedOut: boolean;
  highlightedNodeIds: string[];
  shouldShowUnhighlightableTooltip: boolean;
  x?: number;
  y?: number;
  dragMode: DragMode;
}

const DRAGGABLE_CLONE_PLUGIN_OPTIONS: Partial<ClonePluginOptions> = {
  classes: ['js-vwb-workflow-action-mirror'],
  appendContainer: 'body',
  initialOffset: null,
  cloneElement: null,
  scaleWithTransform: true,
  transparentizeOriginal: true,
};

const AUTOSCROLL_PLUGIN_OPTIONS: Partial<AutoscrollPluginOptions> = {
  scrollableElement: '.js-vwb-canvas',
};

export default class VisualWorkflowBuilderNodeExit extends Component<VisualWorkflowBuilderNodeExitArgs> {
  @service analytics!: AnalyticsService;
  @service auth!: AuthService;
  @service visualWorkflowBuilder!: VisualWorkflowBuilderService;

  get _canClickExit() {
    return this.args.action.isExit && !isNone(this.args.branchId) && !this.args.readOnly;
  }

  get _isEditingThis() {
    return this.args.action === this.args.selectedAction;
  }

  @action
  onClick(event: MouseEvent) {
    this.analytics.track('campaign', 'workflow_existing_item_clicked', {
      model: this.args.action.campaign,
      action: this.args.action,
      trayTab: this.args.tab,
      source: ClickSource.WorkflowItem,
    });

    if (this.args.onActionClicked != null) {
      this.args.onActionClicked(event, this.args.action);
    }

    if (event.defaultPrevented) {
      return;
    }
  }

  @action
  onDeleteExit(dropdown: BasicDropdown) {
    this.visualWorkflowBuilder.selectExitToDelete(
      this.args.action,
      this.args.branch,
      this.args.branchId,
      this.args.pathIndex,
    );
    this.args.onSelect(this.args.action);
    this.isHoveringOver = false;
    dropdown.actions.close();
  }

  @action
  onDeleteDropdownMouseDown(event: MouseEvent) {
    // if shift key is held while clicking, we're likely trying to
    // deselect/select the exit action in the multiselection.
    // In this case, don't trigger ember basic dropdown
    if (event.shiftKey) {
      event.stopImmediatePropagation();
      return false;
    }

    return true;
  }

  isHoveringOver = false;
  unhighlightableTooltipDropdown: BasicDropdown | null = null;

  @action
  onInsertBasicDropdown(_element: HTMLElement, [dropdown]: [BasicDropdown]) {
    this.unhighlightableTooltipDropdown = dropdown;
  }

  @action
  onBasicDropdownOpen() {
    return this.args.shouldShowUnhighlightableTooltip;
  }

  @action
  onShouldShowUnhighlightableTooltipChange() {
    if (!this.unhighlightableTooltipDropdown) {
      return;
    }

    if (
      this.args.shouldShowUnhighlightableTooltip &&
      this.isHoveringOver &&
      !this.unhighlightableTooltipDropdown.isOpen
    ) {
      this.unhighlightableTooltipDropdown.actions.open();
    } else {
      this.unhighlightableTooltipDropdown.actions.close();
    }
  }

  @action
  onMouseEnterTrigger() {
    this.isHoveringOver = true;

    if (this.args.shouldShowUnhighlightableTooltip) {
      this.unhighlightableTooltipDropdown?.actions.open();
      return true;
    }

    return false;
  }

  @action
  onMouseLeaveTrigger() {
    this.isHoveringOver = false;
    this.unhighlightableTooltipDropdown?.actions.close();
  }

  @action
  onBeforeDragStart() {
    this.updatePluginOptions();
  }

  @action
  onDragStart() {
    this.args.onDragStart(this.args.action);
  }

  @computed('args.{dragMode,highlightedNodeIds.[]}')
  get copyHighlighted() {
    return this.args.dragMode === DragMode.Copy && (this.args.highlightedNodeIds ?? []).includes(this.args.action.id);
  }

  @computed('args.{dragMode,highlightedNodeIds.[]}')
  get moveHighlighted() {
    return this.args.dragMode === DragMode.Move && (this.args.highlightedNodeIds ?? []).includes(this.args.action.id);
  }

  @tracked pluginOptions = {
    clone: { ...DRAGGABLE_CLONE_PLUGIN_OPTIONS },
    autoscroll: { ...AUTOSCROLL_PLUGIN_OPTIONS },
  };

  @action
  updatePluginOptions() {
    let cloneOptions: Partial<ClonePluginOptions> = {
      ...DRAGGABLE_CLONE_PLUGIN_OPTIONS,
      transparentizeOriginal: this.args.dragMode === DragMode.Move,
    };

    if (this.copyHighlighted) {
      cloneOptions.cloneElement = '.js-vwb-copy-highlighted-nodes-mirror';
      cloneOptions.scaleWithTransform = false;
      cloneOptions.initialOffset = () => ({
        x: 100,
        y: 20,
      });
    } else if (this.moveHighlighted) {
      cloneOptions.cloneElement = '.js-vwb-move-highlighted-nodes-mirror';
      cloneOptions.scaleWithTransform = false;
      cloneOptions.initialOffset = () => ({
        x: 100,
        y: 20,
      });
    } else {
      if (this.args.action.isBranch && this.args.dragMode !== DragMode.Copy) {
        cloneOptions.cloneElement = `.js-clone-node-${this.args.action.id}`;
        cloneOptions.scaleWithTransform = false;
      } else {
        // scaleWithTransform does not work well with a cloneElement
        cloneOptions.scaleWithTransform = true;
      }
    }

    this.pluginOptions = {
      clone: cloneOptions,
      autoscroll: { ...AUTOSCROLL_PLUGIN_OPTIONS },
    };
  }
}
